import { z } from "zod"
import { Provider } from "./channels.js"
import { Note, PermissionState, Tag } from "./contacts.js"
import { Id } from "./ids.js"
import { CommentAssessment, ModerationPolicy, ModerationSuggestion, SuggestionActivity } from "./moderation.js"

export const ConversationStatus = z.enum(["open", "pending", "resolved"])
export type ConversationStatus = z.infer<typeof ConversationStatus>
export const ControlMode = z.enum(["automation", "takeover_pending", "human", "unowned"])
export type ControlMode = z.infer<typeof ControlMode>
export const SubmissionStatus = z.enum(["queued", "dispatching", "accepted", "rejected", "unknown", "cancelled"])
export type SubmissionStatus = z.infer<typeof SubmissionStatus>

export const SUBMISSION_LABELS: Record<SubmissionStatus, string> = {
  queued: "Queued",
  dispatching: "Sending",
  accepted: "Sent",
  rejected: "Not sent",
  unknown: "Delivery unconfirmed",
  cancelled: "Cancelled",
}

export const ConversationSummary = z.object({
  id: Id,
  contactId: Id,
  contactName: z.string(),
  handle: z.string().nullable(),
  provider: Provider,
  connectionId: Id,
  /** The connected account the thread belongs to, so a reply is never sent from the wrong one. */
  accountName: z.string(),
  accountHandle: z.string().nullable(),
  kind: z.literal("dm"),
  status: ConversationStatus,
  controlMode: ControlMode,
  controlEpoch: z.number().int(),
  assigneeMembershipId: Id.nullable(),
  assigneeName: z.string().nullable(),
  handoverRequestedAt: z.iso.datetime().nullable(),
  handoverReason: z.string().nullable(),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.iso.datetime().nullable(),
  lastInboundAt: z.iso.datetime().nullable(),
  unread: z.boolean(),
  /** True while an automated send is claimed but not yet resolved by the provider. */
  inFlight: z.boolean(),
  updatedAt: z.iso.datetime(),
})
export type ConversationSummary = z.infer<typeof ConversationSummary>

export const ThreadMessage = z.object({
  kind: z.literal("message"),
  id: Id,
  sequence: z.number().int(),
  direction: z.enum(["inbound", "outbound"]),
  authorType: z.enum(["customer", "member", "automation", "campaign", "system"]),
  authorName: z.string(),
  text: z.string().nullable(),
  attachments: z.array(z.object({ type: z.string(), mediaObjectId: Id.nullable(), url: z.string().nullable(), name: z.string().optional() })),
  submissionStatus: SubmissionStatus.nullable(),
  submissionReason: z.string().nullable(),
  deliveredAt: z.iso.datetime().nullable(),
  readAt: z.iso.datetime().nullable(),
  occurredAt: z.iso.datetime(),
})
export type ThreadMessage = z.infer<typeof ThreadMessage>

export const ThreadEvent = z.object({
  kind: z.literal("event"),
  id: Id,
  sequence: z.number().int(),
  type: z.string(),
  actorName: z.string(),
  body: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  occurredAt: z.iso.datetime(),
})
export type ThreadEvent = z.infer<typeof ThreadEvent>

export const ThreadItem = z.discriminatedUnion("kind", [ThreadMessage, ThreadEvent])
export type ThreadItem = z.infer<typeof ThreadItem>

export const ConversationContact = z.object({
  id: Id,
  displayName: z.string(),
  email: z.string().nullable(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  /** Recorded by the team on the profile; providers do not supply them. */
  city: z.string().nullable(),
  timezone: z.string().nullable(),
  firstSeenAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime(),
  conversationCount: z.number().int(),
  tags: z.array(Tag),
  /** Newest first, at most five; the profile holds the rest. */
  notes: z.array(Note),
  marketing: PermissionState,
  supportWindowEndsAt: z.iso.datetime().nullable(),
  recentActivity: z.array(z.object({ title: z.string(), occurredAt: z.iso.datetime() })),
})

export const ConversationDetail = z.object({
  conversation: ConversationSummary,
  contact: ConversationContact,
  items: z.array(ThreadItem),
  /** Automated intents claimed before a takeover and still unresolved: the in-flight state (§10). */
  inFlight: z.array(z.object({ intentId: Id, messageId: Id.nullable(), submissionStatus: SubmissionStatus, text: z.string().nullable(), claimedAt: z.iso.datetime().nullable() })),
  activeRun: z.object({ id: Id, automationName: z.string(), version: z.string(), stepLabel: z.string() }).nullable(),
})
export type ConversationDetail = z.infer<typeof ConversationDetail>

export const ConversationsQuery = z.object({
  queue: z.enum(["all", "mine", "unassigned", "automation", "handover", "resolved"]).default("all"),
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})
export type ConversationsQuery = z.infer<typeof ConversationsQuery>

export const ConversationsPage = z.object({
  conversations: z.array(ConversationSummary),
  counts: z.object({ all: z.number().int(), mine: z.number().int(), unassigned: z.number().int(), automation: z.number().int(), handover: z.number().int() }),
})
export type ConversationsPage = z.infer<typeof ConversationsPage>

export const SendMessageInput = z.object({
  text: z.string().trim().min(1).max(2000),
  idempotencyKey: z.string().min(8).max(120),
})
export type SendMessageInput = z.infer<typeof SendMessageInput>

export const SendMessageResult = z.object({ intentId: Id, messageId: Id, submissionStatus: SubmissionStatus, conversation: ConversationSummary })
export type SendMessageResult = z.infer<typeof SendMessageResult>

export const AddConversationNoteInput = z.object({ body: z.string().trim().min(1).max(4000) })
export const AssignInput = z.object({ assigneeMembershipId: Id.nullable() })
export const StatusInput = z.object({ status: ConversationStatus })
export const TakeoverInput = z.object({ note: z.string().trim().max(2000).optional(), assigneeMembershipId: Id.optional(), statusAfter: ConversationStatus.optional() })
export const ReleaseInput = z.object({ confirm: z.literal(true) })
export const RequestHandoverInput = z.object({ reason: z.string().trim().min(1).max(200) })

// ---- Public comments (screen 10) ----

export const CommentReply = z.object({ id: Id, kind: z.enum(["public_reply", "private_reply"]), text: z.string().nullable(), submissionStatus: SubmissionStatus, authorName: z.string(), createdAt: z.iso.datetime(), /** "vienna" for replies sent here; "provider" for replies made in Instagram or Facebook and synchronised back. */ source: z.enum(["vienna", "provider"]) })
export type CommentReply = z.infer<typeof CommentReply>
/** The post the comment was left on; the thumbnail is a copy in Vienna's storage, never the provider URL. */
export const CommentMedia = z.object({ externalId: z.string(), permalink: z.string().nullable(), caption: z.string().nullable(), mediaType: z.string().nullable(), thumbnailUrl: z.string().nullable() })
export type CommentMedia = z.infer<typeof CommentMedia>

export const CommentSummary = z.object({
  id: Id,
  connectionId: Id,
  provider: Provider,
  accountHandle: z.string().nullable(),
  contactId: Id,
  contactName: z.string(),
  handle: z.string().nullable(),
  text: z.string().nullable(),
  status: z.enum(["new", "handled", "hidden", "deleted"]),
  mediaExternalId: z.string().nullable(),
  commentExternalId: z.string(),
  parentCommentExternalId: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  /** Pending or last action from the ledger, so the UI can show what is in flight. */
  pendingAction: z.object({ kind: z.string(), submissionStatus: SubmissionStatus }).nullable(),
  moderation: z.array(z.object({ ruleName: z.string(), outcome: z.string(), createdAt: z.iso.datetime() })),
  /** Every reply the team or an automation sent for this comment, with its ledger state. Nothing is archived. */
  replies: z.array(CommentReply),
  /** Set when the comment was left on a paid ad; the title is the ad name from Ads Manager. */
  ad: z.object({ id: z.string(), title: z.string().nullable() }).nullable(),
  media: CommentMedia.nullable(),
  /** The latest classifier reading and what policy suggested; null until the assessment job has run. */
  assessment: CommentAssessment.nullable(),
  suggestions: z.array(ModerationSuggestion),
  lastHumanActionAt: z.iso.datetime().nullable(),
})
export type CommentSummary = z.infer<typeof CommentSummary>

export const CommentsPage = z.object({ comments: z.array(CommentSummary), total: z.number().int() })
export type CommentsPage = z.infer<typeof CommentsPage>
export const CommentActionInput = z.object({
  action: z.enum(["private_reply", "public_reply", "hide", "unhide", "delete", "mark_handled", "reopen"]),
  text: z.string().trim().max(2000).optional(),
  idempotencyKey: z.string().min(8).max(120),
})
export type CommentActionInput = z.infer<typeof CommentActionInput>

// ---- Saved replies (43) ----

export const SavedReply = z.object({ id: Id, name: z.string(), shortcut: z.string(), text: z.string(), updatedAt: z.iso.datetime() })
export type SavedReply = z.infer<typeof SavedReply>
export const SaveReplyInput = z.object({ name: z.string().trim().min(1).max(80), shortcut: z.string().trim().min(2).max(40).regex(/^\/[a-z0-9_-]+$/, "Shortcuts start with / and use lowercase letters"), text: z.string().trim().min(1).max(2000) })
export type SaveReplyInput = z.infer<typeof SaveReplyInput>

// ---- Moderation (44, 51) ----

export const ModerationRuleStatus = z.enum(["draft", "enabled", "paused"])
export const ModerationRule = z.object({
  id: Id,
  connectionId: Id,
  accountHandle: z.string(),
  name: z.string(),
  scope: z.enum(["all_posts", "selected_posts"]),
  postIds: z.array(z.string()),
  phrases: z.array(z.string()),
  actions: z.object({ hide: z.boolean(), tagName: z.string().nullable(), notify: z.boolean() }),
  status: ModerationRuleStatus,
  /** Enabled rules only run while the account has comment access. */
  effectiveStatus: z.enum(["draft", "enabled", "paused_access_missing", "paused"]),
  revision: z.number().int(),
  priority: z.number().int(),
  lastEditedByName: z.string(),
  updatedAt: z.iso.datetime(),
})
export type ModerationRule = z.infer<typeof ModerationRule>
export const SaveModerationRuleInput = z.object({
  connectionId: Id,
  name: z.string().trim().min(1).max(80),
  scope: z.enum(["all_posts", "selected_posts"]).default("all_posts"),
  postIds: z.array(z.string().min(1)).max(50).default([]),
  phrases: z.array(z.string().trim().min(1).max(120)).min(1).max(100),
  actions: z.object({ hide: z.boolean(), tagName: z.string().trim().max(40).nullable(), notify: z.boolean() }),
  priority: z.number().int().min(1).max(1000).optional(),
})
export type SaveModerationRuleInput = z.infer<typeof SaveModerationRuleInput>
export const ModerationTestInput = z.object({ phrases: z.array(z.string()).min(1), sample: z.string().min(1).max(2000) })
export const ModerationTestResult = z.object({ matched: z.string().nullable() })
export const ModerationActivity = z.object({ id: Id, ruleName: z.string(), commentId: Id, commentText: z.string().nullable(), outcome: z.string(), createdAt: z.iso.datetime() })
export type ModerationActivity = z.infer<typeof ModerationActivity>
export const ModerationPage = z.object({ rules: z.array(ModerationRule), activity: z.array(ModerationActivity), policy: ModerationPolicy, suggestions: z.array(SuggestionActivity) })
export type ModerationPage = z.infer<typeof ModerationPage>

// ---- Overview (08) ----

export const OverviewMetrics = z.object({
  period: z.enum(["7", "30"]),
  needsHumanReply: z.number().int(),
  assignedToMe: z.number().int(),
  unassigned: z.number().int(),
  handovers: z.number().int(),
  automationOwned: z.number().int(),
  openConversations: z.number().int(),
  firstHumanResponseSeconds: z.number().nullable(),
  newContacts: z.number().int(),
  liveAutomations: z.number().int(),
  runsNeedingAttention: z.number().int(),
  series: z.array(z.object({ day: z.string(), all: z.number().int(), instagram: z.number().int(), messenger: z.number().int() })),
  totalConversations: z.number().int(),
  previousTotalConversations: z.number().int(),
})
export type OverviewMetrics = z.infer<typeof OverviewMetrics>

// ---- Handover queue (12) ----

export const HandoverItem = z.object({ conversation: ConversationSummary, reason: z.string(), waitingSince: z.iso.datetime(), lastCustomerMessage: z.string().nullable(), lastCustomerMessageAt: z.iso.datetime().nullable() })
export type HandoverItem = z.infer<typeof HandoverItem>
export const HandoverPage = z.object({ items: z.array(HandoverItem) })
export type HandoverPage = z.infer<typeof HandoverPage>
export const AcceptHandoverInput = z.object({ assigneeMembershipId: Id, statusAfter: ConversationStatus.default("open"), note: z.string().trim().max(2000).optional() })

// ---- Realtime feed ----

export const FeedInvalidation = z.object({ position: z.number().int(), resources: z.array(z.object({ type: z.string(), id: z.string().nullable() })) })
export type FeedInvalidation = z.infer<typeof FeedInvalidation>
